import React, { useMemo } from 'react';
import { IconButton, Stack } from '@mui/material';
import { Dropdown } from 'common/form';
import { getActionOptions } from 'modules/analyse/utils/getActionsOptions';
import { CandidateAnyStatus } from 'modules/analyse/types/CandidateAnyStatus';
import { useRecoilValue } from 'recoil';
import {
  AutomationActionType,
  CandidateStatus,
  RequirementConditionType,
  StatusSet
} from 'generated-graphql/types';
import { useIntegration } from 'services/integration/hooks';
import { statusSetAtom } from 'services/candidate/atoms';
import { getStatusOptions } from 'modules/analyse/utils/getStatusOptions';
import { ActionTypeComponentLoader } from './ActionTypeComponentLoader';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useConfirmationModal } from 'services/modals/hooks';
import { ConfirmationModalType } from 'services/modals/constants';
import { DeleteOutline } from '@mui/icons-material';
import { FormikErrors } from 'formik';
import { atsErrorMessage } from 'modules/analyse/constants/actionsValidationsSchema';
import { AutomationFormItem } from 'modules/analyse/types/AutomationFormItem';

type Props = {
  index: number;
  setFieldValue: (field: string, value: any) => void;
  analysisAutomationItem: AutomationFormItem;
  candidateBatchId?: string;
  atsJobId?: string | null;
  removeActionItem: () => void;
  errors?: FormikErrors<AutomationFormItem>[];
};

const candidateStatusOptionsMapper = ({
  statusSet
}: {
  statusSet: StatusSet | null;
}): { label: string; value: string }[] => {
  const options = getStatusOptions({ statusSet });

  options.push({
    label: 'Any status',
    value: CandidateAnyStatus
  });

  return options;
};

export const ActionSettingItem: React.FC<Props> = ({
  index,
  analysisAutomationItem,
  candidateBatchId,
  setFieldValue,
  atsJobId,
  removeActionItem,
  errors
}) => {
  const { isConnected } = useIntegration();
  const organization = useOrganization();
  const statusSet = useRecoilValue(statusSetAtom);

  const { showModal } = useConfirmationModal();

  const candidateStatus =
    analysisAutomationItem.requirementConditions[0].type ===
    RequirementConditionType.AnyCandidateStatusCondition
      ? CandidateAnyStatus
      : analysisAutomationItem.requirementConditions[0].candidateStatus;

  const actionOptions = useMemo(() => {
    return getActionOptions({
      candidateStatus: !candidateStatus
        ? undefined
        : (candidateStatus as CandidateStatus),
      isConnectedToAts: isConnected,
      jobRecommendationsEnabled:
        organization.jobRecommendationsEnabled === null
          ? undefined
          : organization.jobRecommendationsEnabled
    });
  }, [isConnected, candidateStatus]);

  const handleCandidateStatusChange = (
    value: typeof CandidateAnyStatus | CandidateStatus
  ) => {
    if (value === CandidateAnyStatus) {
      if (
        analysisAutomationItem.action.type === AutomationActionType.UpdateAts ||
        analysisAutomationItem.action.type === AutomationActionType.SendEmail
      ) {
        setFieldValue(
          `[${index}].action.type`,
          AutomationActionType.SendCampaign
        );
      }

      setFieldValue(
        `[${index}].requirementConditions[0].type`,
        RequirementConditionType.AnyCandidateStatusCondition
      );

      setFieldValue(
        `[${index}].requirementConditions[0].candidateStatus`,
        undefined
      );
      return;
    }

    if (analysisAutomationItem.action.type === AutomationActionType.SendEmail) {
      setFieldValue(
        `[${index}].action.type`,
        AutomationActionType.SendCampaign
      );
    }

    setFieldValue(
      `[${index}].requirementConditions[0].type`,
      RequirementConditionType.CandidateStatusCondition
    );

    setFieldValue(`[${index}].requirementConditions[0].candidateStatus`, value);
  };

  const onDeleteClick = () => {
    showModal({
      title: 'Delete action?',
      message: 'This cannot be undone',
      onDestructiveConfirm: removeActionItem,
      modalType: ConfirmationModalType.DESTRUCTIVE,
      cancelText: 'Cancel',
      destructiveText: 'Delete'
    });
  };

  const error = useMemo<
    { message: string; errorType: 'campaign' | 'ats' } | undefined
  >(() => {
    if (
      errors?.[index]?.action?.campaignId &&
      analysisAutomationItem.action.type ===
        AutomationActionType.SendCampaign &&
      !analysisAutomationItem.action.campaignId
    ) {
      return { message: 'Choose a campaign', errorType: 'campaign' };
    }

    if (
      !!errors?.[index]?.action &&
      typeof errors[index].action === 'string' &&
      (errors[index].action as string).includes(atsErrorMessage) &&
      analysisAutomationItem.action.type === AutomationActionType.UpdateAts &&
      !analysisAutomationItem.action.atsInterviewStageId &&
      !analysisAutomationItem.action.atsRejectedReasonId
    ) {
      return { message: 'Choose ATS status', errorType: 'ats' };
    }

    return undefined;
  }, [errors, index, analysisAutomationItem.action]);

  const resetAnalysisAutomationItem = () => {
    setFieldValue(`[${index}].action.campaignId`, undefined);
    setFieldValue(`[${index}].action.atsInterviewStageId`, undefined);
    setFieldValue(`[${index}].action.atsRejectedReasonId`, undefined);
    setFieldValue(`[${index}].action.komboStageId`, undefined);
    setFieldValue(`[${index}].action.komboRejectionReasonId`, undefined);
    setFieldValue(`[${index}].action.automationTaskEmailTemplateId`, undefined);
  };

  return (
    <Stack direction="row" alignItems="start">
      <Stack direction="column" spacing="30px" flex={1}>
        <Stack direction="column" spacing="10px" width="250px">
          <Dropdown
            name="candidateStatus"
            field={{
              label: 'If applicant status is',
              options: candidateStatusOptionsMapper({ statusSet })
            }}
            onChange={handleCandidateStatusChange}
            value={candidateStatus}
          />
        </Stack>

        <Stack direction="column" spacing="15px" sx={{ maxWidth: '380px' }}>
          <Dropdown
            field={{
              label: 'Then',
              options: actionOptions
            }}
            errorMessage={error?.message}
            value={analysisAutomationItem.action.type || ''}
            onChange={(value) => {
              resetAnalysisAutomationItem();
              setFieldValue(`[${index}].action.type`, value);
            }}
          />
          <ActionTypeComponentLoader
            index={index}
            candidateBatchId={candidateBatchId}
            actionType={analysisAutomationItem.action.type}
            analysisAutomationItem={analysisAutomationItem}
            setFieldValue={setFieldValue}
            candidateStatus={candidateStatus}
            atsJobId={atsJobId}
            errorType={error?.errorType}
          />
        </Stack>
      </Stack>
      <IconButton color="error" size="small" onClick={onDeleteClick}>
        <DeleteOutline />
      </IconButton>
    </Stack>
  );
};
